import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import ScrollableTimeline from './ScrollableTimeline';

// Single source of truth: a signup is "active" if it counts toward slot capacity
// and represents a volunteer who is still expected to show up.
const isActiveSignup = (s) => s.status !== 'denied' && s.status !== 'cancelled';

// Lays out timeslots in the daily view: each department gets one or more horizontal
// "tracks" (rows). Within a department, slots that don't overlap share a row;
// overlapping ones get extra sub-rows. Departments are sorted alphabetically.
// Returns { slotRow: { [slotId]: rowIndex }, totalRows }.
function computeDeptTracks(slots, getDeptName) {
  const byDept = {};
  for (const s of slots) {
    (byDept[s.department_id] = byDept[s.department_id] || []).push(s);
  }
  const sortedDeptIds = Object.keys(byDept)
    .map(id => Number(id))
    .sort((a, b) => getDeptName(a).localeCompare(getDeptName(b)));

  const slotRow = {};
  let totalRows = 0;

  for (const deptId of sortedDeptIds) {
    const deptSlots = [...byDept[deptId]].sort(
      (a, b) => new Date(a.start_time) - new Date(b.start_time)
    );
    const trackEnds = []; // each entry = the end time (ms) of last slot on that track
    for (const slot of deptSlots) {
      const start = new Date(slot.start_time).getTime();
      const end = new Date(slot.end_time).getTime();
      // First track whose last slot ends at or before this slot's start.
      let trackIdx = trackEnds.findIndex(e => e <= start);
      if (trackIdx === -1) {
        trackEnds.push(end);
        trackIdx = trackEnds.length - 1;
      } else {
        trackEnds[trackIdx] = end;
      }
      slotRow[slot.id] = totalRows + trackIdx + 1; // CSS grid rows are 1-indexed
    }
    totalRows += trackEnds.length;
  }

  return { slotRow, totalRows };
}

function DepartmentAdminView({ departmentId, username, onLogout }) {
  const [activeTab, setActiveTab] = useState('timeline');
  const [signups, setSignups] = useState([]);
  const [timeslots, setTimeslots] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('date');
  const [nameFilter, setNameFilter] = useState('');
  const [editingSignup, setEditingSignup] = useState(null);
  const [editTimes, setEditTimes] = useState({ start: '', end: '' });
  const [denyingSignup, setDenyingSignup] = useState(null);
  // Show/hide cross-dept on-call shifts. Only relevant for dept_admins of
  // on-call viewer departments; for everyone else there are no cross-dept
  // slots in the payload and the toggle UI stays hidden.
  const [showOnCall, setShowOnCall] = useState(true);
  // Department filter for global-viewer admins (e.g. Lower Decks), who receive
  // every department's shifts. '' means "All Departments". Ignored for everyone
  // else, whose payload only contains their own (± on-call) slots.
  const [deptFilter, setDeptFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, [departmentId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/timeslots/admin/data`);
      // Backend already scopes to caller's department for dept_admins
      setTimeslots(res.data.timeslots);
      setSignups(res.data.signups);
      setDepartments(res.data.departments);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (signupId) => {
    try {
      await axios.post(`${API_URL}/timeslots/signup/${signupId}/approve`, {
        approved_by: username
      });
      fetchData(); // Refresh
    } catch (error) {
      console.error('Error approving signup:', error);
      // Surface the backend's "shift not ended" message (or any other rejection)
      // so the dept admin understands why their click didn't take effect.
      const msg = error.response?.data?.error;
      if (msg) alert(msg);
    }
  };

  const getDeptName = (id) => {
    const dept = departments.find(d => d.id === id);
    return dept ? dept.name : 'Unknown';
  };

  // Dept admins can only approve once the shift has ended. If the times were
  // edited per-signup, use the edited end; otherwise the slot's end_time.
  // Note: this is evaluated on render — the button won't auto-enable when the
  // clock crosses end_time; the admin needs to refresh (or take some action
  // that triggers a refetch). Backend enforces the same rule regardless.
  const canApproveYet = (signup, slot) => {
    const endStr = signup?.actual_end_time || slot?.end_time;
    if (!endStr) return true;
    return new Date(endStr).getTime() <= Date.now();
  };

  const handleDeny = (signup) => {
    setDenyingSignup(signup);
  };

  const confirmDeny = async () => {
    if (!denyingSignup) return;
    const signupId = denyingSignup.id;
    setDenyingSignup(null);
    try {
      await axios.post(`${API_URL}/timeslots/signup/${signupId}/deny`, {
        approved_by: username
      });
      fetchData(); // Refresh
    } catch (error) {
      console.error('Error denying signup:', error);
    }
  };

  const toDatetimeLocal = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.replace(' ', 'T').slice(0, 16);
  };

  const handleEditTimes = async () => {
    if (!editingSignup) return;
    try {
      await axios.put(`${API_URL}/timeslots/signup/${editingSignup.id}`, {
        actual_start_time: editTimes.start,
        actual_end_time: editTimes.end
      });
      setEditingSignup(null);
      fetchData();
    } catch (error) {
      console.error('Error updating signup:', error);
    }
  };

  const openEditModal = (signup, slot) => {
    setEditingSignup(signup);
    setEditTimes({
      start: toDatetimeLocal(slot?.start_time),
      end: toDatetimeLocal(slot?.end_time)
    });
  };

  const formatShift = (slot, signup = null) => {
    if (!slot) return 'Unknown';
    const startStr = signup?.actual_start_time || slot.start_time;
    const endStr = signup?.actual_end_time || slot.end_time;
    const s = new Date(startStr);
    const e = new Date(endStr);
    const timeOpts = { hour: 'numeric', minute: '2-digit', hour12: true };
    const startTime = s.toLocaleString('en-US', timeOpts);
    const endTime = e.toLocaleString('en-US', timeOpts);
    const multiDay = s.getDate() !== e.getDate() || s.getMonth() !== e.getMonth();
    if (multiDay) {
      const d = { month: 'short', day: 'numeric' };
      return `${s.toLocaleString('en-US', d)} ${startTime} – ${e.toLocaleString('en-US', d)} ${endTime}`;
    }
    const dateLabel = s.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const actualMarker = signup?.actual_start_time ? ' *' : '';
    return `${dateLabel}  •  ${startTime} – ${endTime}${actualMarker}`;
  };

  const statusOrder = { pending: 0, approved: 1 };

  // A global viewer (e.g. Lower Decks) receives every department's shifts and
  // gets a super-admin-style department filter instead of the on-call toggle.
  const ownDept = departments.find(d => d.id === departmentId);
  const isGlobalViewer = !!ownDept?.is_global_viewer;

  // True if any slot in the payload belongs to a different department.
  // Drives whether the on-call show/hide toggle is rendered at all.
  const hasOnCallSlots = timeslots.some(t => t.department_id !== departmentId);

  // Slots to render in the timeline + signups to render in the list. Global
  // viewers filter by the selected department (or see all); everyone else
  // honors the showOnCall toggle. Filtering at this layer means hidden signups
  // also disappear from sort/name-filter results.
  const visibleTimeslots = isGlobalViewer
    ? (deptFilter
        ? timeslots.filter(t => t.department_id === Number(deptFilter))
        : timeslots)
    : (showOnCall
        ? timeslots
        : timeslots.filter(t => t.department_id === departmentId));
  const visibleTimeslotIds = new Set(visibleTimeslots.map(t => t.id));

  // Operational view: only show active signups (cancelled/denied are in the reports tab for history).
  const sortedSignups = signups
    .filter(s => isActiveSignup(s))
    .filter(s => visibleTimeslotIds.has(s.timeslot_id))
    .filter(s => s.name.toLowerCase().includes(nameFilter.toLowerCase().trim()))
    .sort((a, b) => {
      const statusDiff = (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      if (sortBy === 'person') return a.name.localeCompare(b.name);
      const slotA = timeslots.find(t => t.id === a.timeslot_id);
      const slotB = timeslots.find(t => t.id === b.timeslot_id);
      return new Date(slotA?.start_time) - new Date(slotB?.start_time);
    });

  if (loading) return <div>Loading...</div>;

  return (
    <div className="dept-admin-container">
      <div className="admin-header">
        
        <div className="admin-title-section">
          <h1>Department Admin Dashboard</h1>
          <p className="admin-user">
            Logged in as: <strong>{username}</strong>
          </p>
        </div>

        <button className="logout-btn" onClick={onLogout}>
          Logout
        </button>

      </div>

      <nav className="admin-nav">
        <button
          className={activeTab === 'timeline' ? 'active' : ''}
          onClick={() => setActiveTab('timeline')}
        >
          Timeline
        </button>
        <button 
          className={activeTab === 'list' ? 'active' : ''}
          onClick={() => setActiveTab('list')}
        >
          List View
        </button>
      </nav>

      <main className="admin-content">
        {isGlobalViewer ? (
          <div className="dept-filter-control">
            <label>Filter by Department</label>
            <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
              <option value="">All Departments</option>
              {[...departments].sort((a, b) => a.name.localeCompare(b.name)).map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
        ) : hasOnCallSlots && (
          <div className="on-call-toggle">
            <button
              type="button"
              className={`on-call-toggle-btn ${showOnCall ? 'is-on' : 'is-off'}`}
              onClick={() => setShowOnCall(v => !v)}
            >
              {showOnCall ? 'Hide on-call shifts' : 'Show on-call shifts'}
            </button>
          </div>
        )}

        {activeTab === 'timeline' && (
          <DeptTimelineView
            timeslots={visibleTimeslots}
            signups={signups}
            departments={departments}
            ownDepartmentId={departmentId}
            isGlobalViewer={isGlobalViewer}
          />
        )}

        {activeTab === 'list' && (
          <div className="list-view">
            <div className="sort-controls">
              <input
                type="text"
                className="list-name-filter"
                placeholder="Filter by name..."
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
              />
              <label>Sort by:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="date">Date/Time</option>
                <option value="person">Person</option>
              </select>
            </div>

            <table className="signups-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Shift</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedSignups.map(signup => {
                  const slot = timeslots.find(t => t.id === signup.timeslot_id);
                  // Cross-dept on-call signups are read-only for this admin —
                  // the backend allows visibility but rejects mutations.
                  const isViewOnly = slot && slot.department_id !== departmentId;
                  return (
                    <tr key={signup.id}>
                      <td data-label="Name">{signup.name}</td>
                      <td data-label="Phone">{signup.phone}</td>
                      <td data-label="Shift">
                        {formatShift(slot, signup)}
                        {isViewOnly && (
                          <div className="shift-notes"><em>{isGlobalViewer ? 'View only' : 'On-call (view only)'} — {getDeptName(slot.department_id)}</em></div>
                        )}
                        {slot?.notes && (
                          <div className="shift-notes">{slot.notes}</div>
                        )}
                      </td>
                      <td data-label="Status">{signup.status}</td>
                      <td data-label="Actions">
                        {signup.status === 'pending' && !isViewOnly && (() => {
                          const approvable = canApproveYet(signup, slot);
                          const lockedMsg = 'Shifts cannot be approved until their end date/time has passed. If this shift has ended early please edit the time accordingly.';
                          return (
                            <>
                              <button
                                className={`action-btn approve-btn ${approvable ? '' : 'is-locked'}`}
                                onClick={() => approvable ? handleApprove(signup.id) : alert(lockedMsg)}
                                title={approvable ? '' : lockedMsg}
                              >
                                APPROVE
                              </button>
                              <button className="action-btn deny-btn" onClick={() => handleDeny(signup)}>DENY</button>
                              <button className="action-btn edit-btn" onClick={() => openEditModal(signup, slot)}>EDIT</button>
                            </>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {denyingSignup && (
        <div className="edit-time-modal-overlay" onClick={() => setDenyingSignup(null)}>
          <div className="edit-time-modal" onClick={e => e.stopPropagation()}>
            <div className="edit-time-modal-header">
              <h3 className="edit-time-modal-title">Deny Shift?</h3>
              <button className="edit-time-modal-close" onClick={() => setDenyingSignup(null)}>✕</button>
            </div>
            <p className="edit-time-modal-name">{denyingSignup.name}</p>
            <p className="deny-confirm-body">
              Deny this shift? If they showed up for a different time, consider editing the shift instead.
            </p>
            <div className="deny-confirm-actions">
              <button className="deny-confirm-btn deny-confirm-cancel" onClick={() => setDenyingSignup(null)}>CANCEL</button>
              <button className="deny-confirm-btn deny-confirm-deny" onClick={confirmDeny}>DENY</button>
            </div>
          </div>
        </div>
      )}

      {editingSignup && (
        <div className="edit-time-modal-overlay" onClick={() => setEditingSignup(null)}>
          <div className="edit-time-modal" onClick={e => e.stopPropagation()}>
            <div className="edit-time-modal-header">
              <h3 className="edit-time-modal-title">Edit Shift Times</h3>
              <button className="edit-time-modal-close" onClick={() => setEditingSignup(null)}>✕</button>
            </div>
            <p className="edit-time-modal-name">{editingSignup.name}</p>
            <div className="edit-time-fields">
              <label className="edit-time-label">Start</label>
              <input
                className="edit-time-input"
                type="datetime-local"
                value={editTimes.start}
                onChange={e => setEditTimes(t => ({ ...t, start: e.target.value }))}
              />
              <label className="edit-time-label">End</label>
              <input
                className="edit-time-input"
                type="datetime-local"
                value={editTimes.end}
                onChange={e => setEditTimes(t => ({ ...t, end: e.target.value }))}
              />
            </div>
            <div className="edit-time-actions">
              <button className="action-btn deny-btn" onClick={() => setEditingSignup(null)}>CANCEL</button>
              <button className="action-btn approve-btn" onClick={handleEditTimes}>SAVE</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function DeptTimelineView({ timeslots, signups, departments, ownDepartmentId, isGlobalViewer }) {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const viewOnlyLabel = isGlobalViewer ? 'view only' : 'on-call, view only';

  if (timeslots.length === 0) {
    return <div className="no-data">No timeslots to display</div>;
  }

  // Only active signups count toward filled capacity / appear in the slot modal.
  const signupsBySlot = signups.reduce((acc, signup) => {
    if (!isActiveSignup(signup)) return acc;
    if (!acc[signup.timeslot_id]) acc[signup.timeslot_id] = [];
    acc[signup.timeslot_id].push(signup);
    return acc;
  }, {});

  // Group by date — overnight shifts appear on both days. Day 1 is tagged
  // _wrap='start' (forward-arrow hint), day 2 is _wrap='end' (back-arrow hint).
  const slotsByDate = {};
  timeslots.forEach(slot => {
    const startDay = (slot.start_time || '').slice(0, 10);
    const endDay = (slot.end_time || '').slice(0, 10);
    if (!startDay) return;
    const wraps = endDay && endDay !== startDay;
    (slotsByDate[startDay] = slotsByDate[startDay] || []).push(
      wraps ? { ...slot, _wrap: 'start' } : slot
    );
    if (wraps) {
      (slotsByDate[endDay] = slotsByDate[endDay] || []).push({ ...slot, _wrap: 'end' });
    }
  });

  const sortedDates = Object.keys(slotsByDate).sort();

  const getDeptName = (id) => {
    const dept = departments.find(d => d.id === id);
    return dept ? dept.name : 'Unknown';
  };

  const getCapacityColor = (slot) => {
    const filled = (signupsBySlot[slot.id] || []).length;
    const pct = (filled / slot.max_volunteers) * 100;
    if (pct === 0) return 'empty';
    if (pct < 50) return 'low';
    if (pct < 100) return 'medium';
    return 'full';
  };

  const formatSlotTime = (startStr, endStr) => {
    const s = new Date(startStr);
    const e = new Date(endStr);
    const opts = { hour: 'numeric', minute: '2-digit', hour12: true };
    const startLabel = s.toLocaleString('en-US', opts);
    const endLabel = e.toLocaleString('en-US', opts);
    if (s.getDate() !== e.getDate()) {
      return `${s.toLocaleDateString()} ${startLabel} – ${e.toLocaleDateString()} ${endLabel}`;
    }
    return `${startLabel} – ${endLabel}`;
  };

  const slotSignups = selectedSlot ? (signupsBySlot[selectedSlot.id] || []) : [];

  return (
    <div className="daily-view">
      {sortedDates.map(date => (
        <div key={date} className="daily-schedule">
          <div className="schedule-date">
            {/* Anchor to noon local time — bare 'YYYY-MM-DD' parses as UTC midnight,
                which renders as the previous evening in Eastern time. */}
            {new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
              weekday: 'long', month: 'long', day: 'numeric'
            })}
          </div>

          <ScrollableTimeline>
            <div className="hours-labels">
              {Array.from({ length: 24 }, (_, hour) => {
                const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                const period = hour >= 12 ? 'p' : 'a';
                return (
                  <div
                    key={hour}
                    className="hour-label"
                    style={{ left: `${(hour * 100) / 24}%` }}
                  >
                    {display}{period}
                  </div>
                );
              })}
            </div>

            {(() => {
              const { slotRow, totalRows } = computeDeptTracks(slotsByDate[date], getDeptName);
              return (
                <div
                  className="timeline-slots"
                  style={{ gridTemplateRows: `repeat(${totalRows}, minmax(40px, auto))` }}
                >
                  {slotsByDate[date].map(slot => {
                    // Parse H:M directly from ISO string — no timezone juggling.
                    // Grid is 1440 cols (one per minute), so a 9:30 slot lands
                    // halfway between the "9a" and "10a" tick marks rather than
                    // snapping to 9:00.
                    const sH = parseInt((slot.start_time || '').slice(11, 13), 10) || 0;
                    const sM = parseInt((slot.start_time || '').slice(14, 16), 10) || 0;
                    const eH = parseInt((slot.end_time || '').slice(11, 13), 10) || 0;
                    const eM = parseInt((slot.end_time || '').slice(14, 16), 10) || 0;
                    const start = sH * 60 + sM;
                    let end = eH * 60 + eM;
                    if (end <= start) end += 24 * 60;  // crosses midnight
                    const DAY = 24 * 60;
                    // Continuation row (_wrap='end') shows the post-midnight portion.
                    const colStart = slot._wrap === 'end' ? 0 : start;
                    const colEnd = slot._wrap === 'end' ? Math.min(end - DAY, DAY) : Math.min(end, DAY);
                    const duration = Math.max(colEnd - colStart, 1);
                    const color = getCapacityColor(slot);
                    const filled = (signupsBySlot[slot.id] || []).length;
                    const isViewOnly = slot.department_id !== ownDepartmentId;
                    return (
                      <div
                        key={`${slot.id}-${slot._wrap || 'start'}`}
                        className={`timeline-slot ${color} ${slot._wrap ? 'slot-wrap-' + slot._wrap : ''} ${isViewOnly ? 'slot-view-only' : ''}`}
                        style={{
                          gridColumn: `${colStart + 1} / span ${duration}`,
                          gridRow: slotRow[slot.id],
                          position: 'relative'
                        }}
                        title={`${getDeptName(slot.department_id)} – ${filled}/${slot.max_volunteers}${isViewOnly ? ` (${viewOnlyLabel})` : ''}`}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        <div className="slot-label">
                          <div className="slot-dept">{getDeptName(slot.department_id)}</div>
                          <div className="slot-capacity">{filled}/{slot.max_volunteers}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </ScrollableTimeline>

          <div className="schedule-legend">
            <div className="legend-item"><div className="legend-color empty"></div><span>No Signups</span></div>
            <div className="legend-item"><div className="legend-color low"></div><span>0–50% Full</span></div>
            <div className="legend-item"><div className="legend-color medium"></div><span>50–100% Full</span></div>
            <div className="legend-item"><div className="legend-color full"></div><span>Full</span></div>
          </div>
        </div>
      ))}

      {selectedSlot && (
        <div className="slot-modal-overlay" onClick={() => setSelectedSlot(null)}>
          <div className="slot-modal" onClick={e => e.stopPropagation()}>
            <div className="slot-modal-header">
              <div>
                <h3>
                  {getDeptName(selectedSlot.department_id)}
                  {selectedSlot.department_id !== ownDepartmentId && (
                    <span className="slot-view-only-badge"> · {isGlobalViewer ? 'View only' : 'On-call (view only)'}</span>
                  )}
                </h3>
                <div className="slot-modal-time">{formatSlotTime(selectedSlot.start_time, selectedSlot.end_time)}</div>
              </div>
              <button className="slot-modal-close" onClick={() => setSelectedSlot(null)}>✕</button>
            </div>
            <div className="slot-modal-body">
              {selectedSlot.notes && (
                <div className="slot-modal-notes">{selectedSlot.notes}</div>
              )}
              <div className="slot-modal-count">
                {slotSignups.length} / {selectedSlot.max_volunteers} volunteers
              </div>
              {slotSignups.length === 0 ? (
                <div className="slot-modal-empty">No signups for this slot yet.</div>
              ) : (
                slotSignups.map((vol, idx) => (
                  <div key={vol.id} className="slot-modal-volunteer">
                    <div className="slot-modal-vol-number">{idx + 1}</div>
                    <div>
                      <div className="slot-modal-vol-name">{vol.name}</div>
                      <div className="slot-modal-vol-phone">{vol.phone}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DepartmentAdminView;